import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router";
import { MonitorOff, Radio } from "lucide-react";

import { useNow } from "../../lib/useNow.js";
import { feedOf, useBoard, useScreenKey } from "./board.js";
import { Bridge } from "./layouts/Bridge.jsx";
import { Stage } from "./layouts/Stage.jsx";
import { Stream } from "./layouts/Stream.jsx";

/**
 * The wall.
 *
 * A screen nobody touches, read from across a room: what is happening on the
 * box right now. Three layouts; `/live/<n>` shows one, `/live` shows the one
 * chosen in Settings and follows the choice when it changes, so the screen is
 * never touched to change it.
 *
 * It stands outside the dashboard. Dark whatever the system says, no sidebar,
 * no cursor, and the root font size follows the screen's width so that every
 * rem on the board scales together — a 4K panel and a 1080p one both read.
 */

const LAYOUTS = { 1: Bridge, 2: Stage, 3: Stream };

export function Live() {
    const params = useParams();
    const key = useScreenKey();
    const { board, problem, stale } = useBoard(key);
    const now = useNow();

    const forced = LAYOUTS[Number(params.layout)] ? Number(params.layout) : null;
    const target = forced ?? board?.layout ?? 1;
    const [shown, setShown] = useState(target);

    // A change of layout crosses over rather than cuts, where the browser can.
    useEffect(() => {
        if (shown === target) return;
        const swap = () => flushSync(() => setShown(target));
        if (document.startViewTransition) document.startViewTransition(swap);
        else swap();
    }, [target, shown]);

    useEffect(() => {
        const html = document.documentElement;
        const previous = { fontSize: html.style.fontSize, title: document.title };
        html.style.fontSize = "clamp(14px, 1vw, 28px)";
        document.title = "ops · live";
        return () => {
            html.style.fontSize = previous.fontSize;
            document.title = previous.title;
        };
    }, []);

    const projects = useMemo(() => new Map((board?.projects ?? []).map((project) => [project.slug, project])), [board]);
    const feed = useMemo(() => feedOf(board), [board]);
    const Layout = LAYOUTS[shown];

    return (
        <div data-live data-live-layout={shown} className="fixed inset-0 cursor-none select-none overflow-hidden bg-gray-950 text-gray-100 antialiased">
            {/* A faint grid fading out towards the edges, a wash of blue at the top, a slow sweep down the screen. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:3.2rem_3.2rem] [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_85%)]" />
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.10),transparent_55%)]" />
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[18vh] animate-scan bg-gradient-to-b from-transparent via-white/[0.025] to-transparent" />

            <div className="relative h-full p-[1.1rem]">
                {problem === "unpaired" ? (
                    <Unpaired hasKey={Boolean(key)} />
                ) : !board ? (
                    <Connecting />
                ) : (
                    <div key={shown} className="h-full animate-rise">
                        <Layout board={board} projects={projects} feed={feed} screenKey={key} now={now} />
                    </div>
                )}
            </div>

            {board && stale ? <Pill tone="amber">reconnecting</Pill> : null}
            {board?.degraded ? <Pill tone="amber">database unavailable — showing what memory holds</Pill> : null}
        </div>
    );
}

function Pill({ tone, children }) {
    const colour = tone === "amber" ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-white/10 bg-white/5 text-gray-300";
    return (
        <div className={`absolute bottom-[1.4rem] left-1/2 -translate-x-1/2 animate-rise rounded-full border px-4 py-1.5 text-[0.85rem] ${colour}`}>
            <span className="mr-2 inline-block size-2 animate-breathe rounded-full bg-current align-middle" />
            {children}
        </div>
    );
}

function Connecting() {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-4 text-gray-500">
            <Radio className="size-[3rem] animate-breathe" />
            <div className="live-label">connecting</div>
        </div>
    );
}

/** The one thing the wall says about itself: that it is not allowed to see. */
function Unpaired({ hasKey }) {
    return (
        <div className="flex h-full items-center justify-center">
            <div className="live-card max-w-[34rem] p-[2.4rem] text-center">
                <MonitorOff className="mx-auto size-[3rem] text-gray-500" />
                <h1 className="mt-4 text-[1.8rem] font-semibold tracking-tight">{hasKey ? "This screen's key no longer works" : "This screen is not paired"}</h1>
                <p className="mt-3 text-[1rem] leading-relaxed text-gray-400">
                    {hasKey
                        ? "Its key was revoked. Pair it again from Settings → Live on a signed-in device and open the new link here once."
                        : "Pair it from Settings → Live on a signed-in device and open the link it gives you here, once. Or sign in on this device to preview the board."}
                </p>
            </div>
        </div>
    );
}
